import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import { BrollCaptionVideo, Status } from '../../@types';
import {
  BrollRenderStatus,
  handleDownloadVideoFile,
  MediaStatus,
  STATUS_REGISTRATION_REQUIREMENT,
} from '../../utils';
import { useAppContext, useDialog, useGetBrollCaptionProgress } from '../../hooks';
import { useDeleteBrollVideo } from '../../services/broll-videos/deleteVideo';
import { useCloneBrollCaption, useUpdateBrollVideoName } from '../../services';

interface Props {
  videoData: BrollCaptionVideo;
  refreshBrollData: () => void;
  handleOnPreview: (_: string) => void;
  handleEdit?: (id: string) => void;
}

export const useBrollCaptionCardActions = ({
  videoData,
  refreshBrollData,
  handleOnPreview,
  handleEdit,
}: Props) => {
  const { id, name, renderedUrl, status, renderStatus } = videoData;

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [overallPercentage, setOverallPercentage] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const { push } = useRouter();

  const {
    websocket: { isConnected, sendMessage, brollCaptionTaskMap },
  } = useAppContext();

  const { videoDataStatus } = useGetBrollCaptionProgress({
    id,
    setOverallPercentage,
    initialStatus: renderStatus === BrollRenderStatus.InDraft ? status : renderStatus,
    isRendering: renderStatus !== BrollRenderStatus.InDraft,
    refreshBrollData,
  });

  const {
    isDialogOpen: isDeleteDialogOpen,
    openDialog: openDeleteDialog,
    closeDialog: closeDeleteDialog,
  } = useDialog();

  const {
    isDialogOpen: isCloneDialogOpen,
    openDialog: openCloneDialog,
    closeDialog: closeCloneDialog,
  } = useDialog();

  const {
    isDialogOpen: isRenameDialogOpen,
    openDialog: openRenameDialog,
    closeDialog: closeRenameDialog,
  } = useDialog();

  const { mutate: deleteVideo, isLoading: isDeletingVideo } = useDeleteBrollVideo();
  const { mutate: mutateCloneBrollCaption, isLoading: isCloninBrollCaption } =
    useCloneBrollCaption();
  const { mutate: mutateEditName, isLoading: isEditNameLoading } = useUpdateBrollVideoName();

  const { registrationRequired } = STATUS_REGISTRATION_REQUIREMENT;

  const isInProgress = registrationRequired.includes(videoDataStatus);

  const confirmDeleteVideo = async () => {
    deleteVideo(id, {
      onSuccess: () => {
        refreshBrollData();
        enqueueSnackbar('Video deleted successfully', { variant: 'success' });
        closeDeleteDialog();
      },
      onError: () => {
        enqueueSnackbar('Error deleting video', { variant: 'error' });
      },
    });
  };

  const goToVideoEditor = () => {
    push(`/edit-video/${id}`);
  };

  const initiateDownloadVideo = () => {
    handleDownloadVideoFile({
      url: renderedUrl,
      name,
      onSuccess: () => setMenuAnchorEl(null),
    });
  };

  const handleOnClone = (name: string) => {
    mutateCloneBrollCaption(
      { videoId: id, name },
      {
        onSuccess: (data) => {
          refreshBrollData();
          closeCloneDialog();
          if (data?.overlayId) {
            enqueueSnackbar('Successfully cloned', { variant: 'success' });
          } else {
            enqueueSnackbar('Cloning in progress...', { variant: 'info' });
          }
        },
        onError: (err: any) => {
          enqueueSnackbar('Error cloning video', { variant: 'error' });
        },
      }
    );
  };

  const handleOnRename = (name: string) => {
    mutateEditName(
      { id, name },
      {
        onSuccess: () => {
          enqueueSnackbar('Name updated successfully!', { variant: 'success' });
          closeRenameDialog();
          refreshBrollData();
        },
        onError: (error: any) => {
          enqueueSnackbar(error?.message || 'Failed to update name. Please try again.', {
            variant: 'error',
          });
          closeRenameDialog();
        },
      }
    );
  };

  const menuActions = [
    {
      id: 'viewBrollCaption',
      label: 'Edit',
      iconifyIcon: 'mdi:format-color-fill',
      isHidden: status !== MediaStatus.Success,
      onClick: () => {handleEdit ? handleEdit(id) : goToVideoEditor()},
    },
    {
      id: 'previewVideo',
      label: 'Preview',
      iconifyIcon: 'mdi:eye-outline',
      isHidden: !renderedUrl,
      onClick: () => {
        handleOnPreview(id);
        setMenuAnchorEl(null);
      },
    },
    {
      id: 'cloneBroll',
      label: 'Clone',
      iconifyIcon: 'material-symbols:content-copy',
      isHidden: status !== MediaStatus.Success,
      onClick: () => {
        openCloneDialog();
        setMenuAnchorEl(null);
      },
    },
    {
      id: 'rename',
      label: 'Rename',
      iconifyIcon: 'mdi:rename',
      isHidden: status !== MediaStatus.Success,
      onClick: () => {
        openRenameDialog();
        setMenuAnchorEl(null);
      },
    },
    {
      id: 'downloadVideo',
      label: 'Download',
      iconifyIcon: 'material-symbols:download',
      isHidden: !renderedUrl,
      onClick: initiateDownloadVideo,
    },
    {
      id: 'deleteVideo',
      label: 'Delete',
      iconifyIcon: 'material-symbols:delete',
      isHidden: isInProgress,
      onClick: () => {
        openDeleteDialog();
        setMenuAnchorEl(null);
      },
    },
  ];

  useEffect(() => {
    const { registrationRequired } = STATUS_REGISTRATION_REQUIREMENT;

    if (isConnected && registrationRequired.includes(videoDataStatus)) {
      sendMessage({
        event: 'register',
        data: {
          id,
        },
      });
    }
  }, [isConnected, videoDataStatus, id]);

  return {
    menuAnchorEl,
    setMenuAnchorEl,
    isDeleteDialogOpen,
    closeDeleteDialog,
    confirmDeleteVideo,
    isDeletingVideo,
    menuActions,
    goToVideoEditor,
    isCloneDialogOpen,
    closeCloneDialog,
    isCloninBrollCaption,
    handleOnClone,
    videoDataStatus,
    overallPercentage: Math.round(overallPercentage),
    hideDropdownButton:
      Boolean(brollCaptionTaskMap.get(id)?.status === Status.InProgress) || isInProgress || renderStatus === BrollRenderStatus.InQueue,
    closeRenameDialog,
    isRenameDialogOpen,
    isEditNameLoading,
    handleOnRename,
  };
};
